import React from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Image,
    VStack,
    HStack,
    Badge,
    Divider,
    useColorModeValue,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';

interface GuideSection {
    id: string;
    title: string;
    badge?: string;
    subsections: {
        subtitle?: string;
        description: string;
        image?: string;
        notes?: string[];
    }[];
}

const UserGuidePage2: React.FC = () => {
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const accentColor = useColorModeValue('blue.500', 'blue.300');
    const hoverBg = useColorModeValue('gray.50', 'gray.700');
    const noteBoxBg = useColorModeValue('blue.50', 'blue.900');

    const guideData: GuideSection[] = [
        // ==================== 1. 대시보드 ====================
        {
            id: 'dashboard',
            title: '대시보드',
            subsections: [
                {
                    subtitle: '대시보드 개요',
                    description: 'AI 챗봇 시스템의 전반적인 현황과 주요 통계를 한눈에 확인할 수 있는 메인 화면입니다.',
                    image: '/guide/dashboard-01.png',
                    notes: [
                        '실시간 쿼리 수, 평균 신뢰도 모니터링',
                        '최근 질문 분석 및 트렌드 확인',
                        '시간대별 사용 패턴 그래프',
                        '건물별 쿼리 분포 확인'
                    ]
                },
                {
                    subtitle: '주요 지표',
                    description: '대시보드에서 확인할 수 있는 주요 지표들입니다:\n\n• 총 쿼리 수: 지금까지 사용자가 질문한 총 횟수\n• 오늘 쿼리 수: 금일 검색된 횟수\n• 평균 신뢰도: 최근 7일간 AI 답변의 품질 지표 (높을수록 좋음)\n• 평균 처리시간: 최근 7일간 AI 응답 속도 (밀리초 단위)',
                    notes: [
                        '통계는 최근 7일간의 데이터를 기준으로 표시됩니다',
                        '평균 신뢰도 70% 이상이면 양호한 품질입니다'
                    ]
                },
                {
                    subtitle: '시스템 상태',
                    description: '하단의 시스템 상태 카드에서 AI 시스템의 연결 상태를 확인할 수 있습니다:\n\n• AI 워크플로우: AI 처리 시스템 정상 여부\n• Google Gemini API: AI 언어 모델 연결 상태\n• ChromaDB: 벡터 데이터베이스 상태',
                    notes: [
                        '모든 항목이 "정상" 또는 "연결됨" 상태여야 합니다',
                        '문제 발생 시 관리자에게 문의하세요'
                    ]
                }
            ]
        },
        // ==================== 2. 쿼리 로그 ====================
        {
            id: 'logs',
            title: '쿼리 로그',
            subsections: [
                {
                    subtitle: '쿼리 로그 개요',
                    description: '사용자가 챗봇에 질문한 내역과 AI 응답을 확인하고 분석할 수 있는 페이지입니다.',
                    image: '/guide/logs.gif',
                    notes: [
                        '사용자 질문과 AI 답변 전체 조회',
                        '다양한 필터로 원하는 로그 검색',
                        '로그 상세 보기 및 FAQ 등록 기능',
                        '검증 상태별 필터링 가능'
                    ]
                },
                {
                    subtitle: '필터 기능',
                    description: '쿼리 로그를 효율적으로 검색하고 필터링할 수 있습니다:\n\n• 검색어 입력: 질문 또는 답변 내용으로 검색 (0.5초 자동 검색)\n• 상태 필터: 성공(SUCCESS), FAQ, 오류(ERROR), 개선필요(NEEDS_IMPROVEMENT)\n• 검증 상태 필터: 정상, 정보없음, 출력형식, 관련성없음, 오답, 통신오류\n• 건물 필터: 1전시장, 2전시장, 컨벤션홀, 오디토리움, 누리마루\n• 날짜 필터: 시작일 ~ 종료일 범위 검색\n• 제외 IP: 특정 IP 주소를 검색 결과에서 제외',
                    notes: [
                        '여러 필터를 동시에 적용 가능',
                        '"필터 초기화" 버튼으로 모든 필터 리셋'
                    ]
                },
                {
                    subtitle: '상태(Status)와 검증 상태(Validation)의 차이',
                    description: '두 가지 상태는 각각 다른 의미를 가집니다:\n\n📊 상태 (RAG Status)\n- 의미: AI 응답 생성의 기술적 성공/실패 여부\n- SUCCESS: 정상적으로 답변 생성\n- FAQ: 자주하는 질문에서 답변 (AI 미사용)\n- ERROR: 시스템 오류로 답변 실패\n- NEEDS_IMPROVEMENT: 답변 품질 개선 필요\n\n🔍 검증 상태 (Validation)\n- 의미: AI 답변 내용의 품질을 자동 검증한 결과\n- 정상(NONE): 적절한 답변\n- 정보없음(NO_ANSWER): 관련 정보를 찾지 못함\n- 출력형식(FORMAT): 형식이 잘못된 답변\n- 관련성없음(IRRELEVANT): 질문과 무관한 답변\n- 오답(WRONG): 잘못된 정보 제공\n- 통신오류(COMMUNICATION): API 에러 등',
                    notes: [
                        '검증 상태는 시스템이 자동으로 판정합니다',
                        '필요시 수동으로 검증 상태를 수정할 수 있습니다'
                    ]
                },
                {
                    subtitle: '로그 상세 보기',
                    description: '테이블에서 시간 또는 질문을 클릭하면 상세 정보를 확인할 수 있습니다:\n\n• 기본 정보: 시간, 사용자 ID, 상태, 신뢰도, 처리시간, 건물, IP 주소\n• 검증 상태: 자동 검증 결과 및 검증 사유\n• 추출된 키워드: AI가 질문에서 추출한 핵심 키워드\n• 질문/답변: 전체 내용 확인',
                    notes: [
                        '검증 사유는 왜 해당 검증 상태로 판정되었는지 설명합니다',
                        '추출된 키워드는 문서 검색에 사용된 핵심 단어입니다'
                    ]
                },
                {
                    subtitle: '정상 확정 / 오답 버튼',
                    description: '로그 상세 보기에서 액션을 수행할 수 있습니다:\n\n✅ 정상 확정: 해당 답변이 올바르다고 판단되면 클릭\n- 검증된 QA 목록에 등록됩니다\n- FAQ 관리 페이지의 추천 목록에 표시됩니다\n\n❌ 오답: 해당 답변이 잘못되었다고 판단되면 클릭\n- 검증 상태가 "오답"으로 변경됩니다\n- 추후 데이터 개선에 활용됩니다',
                    notes: [
                        '정상 확정된 QA는 FAQ로 쉽게 등록할 수 있습니다',
                        '오답으로 마킹된 로그는 데이터 품질 개선에 활용됩니다'
                    ]
                }
            ]
        },
        // ==================== 3. 웹 크롤링 ====================
        {
            id: 'crawler-logs',
            title: '웹 크롤링',
            subsections: [
                {
                    subtitle: '웹 크롤링 개요',
                    description: 'BEXCO 웹사이트에서 최신 정보를 자동으로 수집하는 크롤링 작업 로그를 확인하는 페이지입니다.',
                    image: '/guide/crawler.gif',
                    notes: [
                        '크롤링 성공/실패 로그 확인',
                        '수집된 데이터 건수 통계',
                        '사이트별, 상태별 필터링'
                    ]
                },
                {
                    subtitle: '자동 크롤링 일정',
                    description: '각 사이트별로 자동 크롤링이 예약되어 있습니다:\n\n⏰ 자동 실행 시간\n• 행사일정: 매일 새벽 1시\n• 공지사항: 매일 새벽 3시\n• 뉴스: 매일 새벽 4시',
                    notes: [
                        '자동 크롤링은 매일 새벽에 실행됩니다',
                        '수동 크롤링은 문의게시판을 통해 요청하세요'
                    ]
                },
                {
                    subtitle: '크롤링 로그 확인',
                    description: '크롤링 실행 이력을 상세하게 확인할 수 있습니다:\n\n• 상태: SUCCESS(성공), PARTIAL(부분 성공), FAILED(실패)\n• 실행 유형: MANUAL(수동), SCHEDULED(자동), INITIAL(초기화)\n• 수집 통계: 발견/크롤링/스킵/실패 건수\n• 실행 시간: 시작~완료 시간, 소요 시간',
                    notes: [
                        '"보기" 버튼으로 상세 정보 확인 가능',
                        '오류 발생 시 상세 오류 메시지 확인 가능'
                    ]
                },
                {
                    subtitle: '통계 정보',
                    description: '상단의 통계 카드에서 크롤링 현황을 확인할 수 있습니다:\n\n• 최근 24시간 실행: 최근 하루 동안 실행된 크롤링 횟수\n• 성공/부분 성공/실패: 상태별 건수',
                    notes: [
                        '성공률이 낮으면 웹사이트 구조 변경 등을 확인해야 합니다',
                        '문제 발생 시 관리자에게 문의하세요'
                    ]
                }
            ]
        },
        // ==================== 4. 검증 리포트 ====================
        {
            id: 'validation-report',
            title: '검증 리포트',
            badge: 'AI 테스트',
            subsections: [
                {
                    subtitle: '검증 리포트 개요',
                    description: 'AI 챗봇의 응답 품질을 테스트하고 검증하는 페이지입니다.\n\n미리 등록된 테스트 질문 풀에서 질문을 랜덤으로 추출하여 AI에게 물어보고, 그 결과를 분석합니다.\n\n💡 주요 용도: AI 챗봇이 제대로 답변하는지 정기적으로 테스트할 때 사용합니다.',
                    image: '/guide/validation.gif',
                    notes: [
                        '테스트용 질문 풀에서 랜덤 추출하여 검증',
                        '검증 결과: 정상/정보없음/출력형식/관련성없음/오답/통신오류',
                        '평균 신뢰도 및 응답시간 측정'
                    ]
                },
                {
                    subtitle: '검증 실행하기',
                    description: '1. 테스트할 질문 개수를 선택합니다 (20/50/100/200/500개)\n2. 카테고리를 선택합니다 (전체 또는 특정 카테고리)\n3. "검증" 버튼을 클릭합니다\n4. 검증이 완료되면 결과가 테이블에 표시됩니다',
                    notes: [
                        '질문 개수가 많을수록 시간이 오래 걸립니다',
                        '검증 완료 후 리포트 파일을 새 탭에서 확인할 수 있습니다'
                    ]
                },
                {
                    subtitle: '검증 결과 분류 (6가지)',
                    description: '검증 결과는 다음과 같이 6가지로 분류됩니다:\n\n✅ 정상 (NONE): 올바른 답변\n🟠 정보없음 (NO_ANSWER): 관련 정보를 찾지 못함\n🟡 출력형식 (FORMAT): 형식이 잘못된 답변\n🟣 관련성없음 (IRRELEVANT): 질문과 관련 없는 답변\n🔴 오답 (WRONG): 잘못된 정보 제공\n⚫ 통신오류 (COMMUNICATION): API 에러 등',
                    notes: [
                        '정상 비율이 높을수록 좋은 품질',
                        '오답이 많으면 데이터 보강이 필요합니다',
                        '평균 신뢰도 70% 이상을 권장합니다'
                    ]
                },
                {
                    subtitle: '질문 풀 관리',
                    description: '테스트용 질문을 업로드하여 관리할 수 있습니다:\n\n• 질문 업로드: JSON 또는 TXT 파일로 대량 업로드\n• 카테고리 분류: 위치, 시설, 운영 등',
                    notes: [
                        'JSON, TXT 파일 형식 지원',
                        '카테고리별로 검증 가능'
                    ]
                },
                {
                    subtitle: '질문 파일 업로드 방법',
                    description: '테스트용 질문 파일을 업로드하는 방법입니다:\n\n[지원 파일 형식]\n• JSON 파일 (.json)\n• 텍스트 파일 (.txt)\n\n[JSON 파일 형식]\n[\n  { "question": "제1전시장 위치가 어디인가요?", "category": "위치" },\n  { "question": "주차요금은 얼마인가요?", "category": "운영" }\n]\n\n[TXT 파일 형식]\n한 줄에 하나의 질문을 입력합니다.\n카테고리는 자동으로 "기타"로 분류됩니다.\n\n예시:\n제1전시장 위치가 어디인가요?\n주차요금은 얼마인가요?\n컨벤션홀 수용인원이 몇명인가요?',
                    notes: [
                        'JSON 형식: 카테고리 지정 가능',
                        'TXT 형식: 한 줄에 하나씩, 카테고리는 자동으로 "기타"',
                        '업로드 후 카테고리 수정 가능'
                    ]
                }
            ]
        },
        // ==================== 5. 공지사항 ====================
        {
            id: 'announcements',
            title: '공지사항',
            badge: '게시판',
            subsections: [
                {
                    subtitle: '공지사항',
                    description: '시스템 관련 공지사항을 확인할 수 있는 페이지입니다.\n\n• 공지사항 목록 및 상세 내용 확인 가능\n• 팝업으로 설정된 공지는 로그인 시 자동으로 표시됩니다',
                    image: '/guide/notice.png',
                    notes: [
                        '중요한 공지사항은 정기적으로 확인해주세요'
                    ]
                }
            ]
        },
        // ==================== 6. FAQ 관리 ====================
        {
            id: 'faqs',
            title: '자주하는 질문',
            badge: '관리 메뉴',
            subsections: [
                {
                    subtitle: 'FAQ 관리 개요',
                    description: '자주 묻는 질문과 답변을 등록하고 관리하는 페이지입니다.\n\nFAQ에 등록된 질문은 AI를 거치지 않고 바로 답변이 제공됩니다.',
                    image: '/guide/faqs.gif',
                    notes: [
                        '쿼리 로그에서 검증 확정된 질문만 FAQ로 등록 가능',
                        '카테고리: 방문자, 대관, 디지털트윈',
                        '카테고리별 최대 5개까지 등록 가능'
                    ]
                },
                {
                    subtitle: 'FAQ 등록하기',
                    description: 'FAQ를 등록하는 방법입니다:\n\n[사전 작업: 쿼리 로그에서 검증 확정]\n1. 쿼리 로그 페이지로 이동합니다\n2. AI가 잘 답변한 질문을 찾아 클릭합니다\n3. 상세 보기에서 "정상 확정" 버튼을 클릭합니다\n\n[FAQ 등록]\n1. 자주하는 질문 페이지 하단의 "추천: 검증된 자주 묻는 질문" 목록을 확인합니다\n2. 등록하려는 질문의 "FAQ 등록" 버튼을 클릭합니다\n3. 모달에서 카테고리를 선택하고 질문/답변을 수정합니다\n4. "등록" 버튼을 클릭합니다',
                    notes: [
                        '쿼리 로그에서 먼저 "정상 확정"을 해야 목록에 나타납니다',
                        '검증된 질문만 FAQ로 등록할 수 있습니다'
                    ]
                },
                {
                    subtitle: '카테고리별 5개 제한',
                    description: '각 카테고리마다 최대 5개의 FAQ만 등록할 수 있습니다:\n\n• 방문자: 최대 5개\n• 대관: 최대 5개\n• 디지털트윈: 최대 5개\n\n📢 5개를 초과하여 등록하려고 하면 안내 메시지가 표시됩니다.\n다른 카테고리를 선택하거나 기존 FAQ를 삭제 후 등록해주세요.',
                    notes: [
                        '각 카테고리별로 가장 중요한 질문 5개만 선별하세요',
                        '자주 묻지 않는 질문은 AI가 답변하도록 두세요'
                    ]
                },
                {
                    subtitle: '검증된 QA 추천 목록',
                    description: '하단의 "추천: 검증된 자주 묻는 질문" 섹션에서 쿼리 로그에서 정상 확정된 QA 목록을 확인할 수 있습니다.\n\n🔥 조회수가 높은 순으로 정렬되어 사용자가 자주 묻는 질문을 쉽게 파악할 수 있습니다.\n\n✅ 이미 FAQ에 등록된 질문은 녹색으로 표시되고 "FAQ 등록됨" 배지가 붙습니다.\n\n📌 등록되지 않은 질문의 "FAQ 등록" 버튼을 클릭하면 등록 모달이 열립니다.',
                    notes: [
                        '조회수가 높은 질문을 우선적으로 FAQ에 등록하세요',
                        '질문과 답변 내용은 등록 시 수정 가능합니다'
                    ]
                }
            ]
        },
        // ==================== 7. 문의게시판 ====================
        {
            id: 'inquiry-board',
            title: '문의게시판',
            badge: '소통',
            subsections: [
                {
                    subtitle: '문의게시판 개요',
                    description: 'BEXCO 담당자와 관리자 간의 소통을 위한 게시판입니다.',
                    image: '/guide/inquiry.gif',
                    notes: [
                        '문의 등록, 수정, 삭제 가능',
                        '상태: 문의 → 진행중 → 보류 → 완료',
                        '댓글 기능으로 실시간 소통'
                    ]
                },
                {
                    subtitle: '문의 등록하기',
                    description: '1. "문의 등록" 버튼을 클릭합니다\n2. 제목과 내용을 입력합니다\n3. 내용은 Rich Text Editor로 작성합니다\n4. 이미지 업로드가 가능합니다 (최대 10MB)\n5. "등록" 버튼을 클릭합니다',
                    notes: [
                        '이미지 첨부 시 최대 10MB',
                        '완료된 문의는 기록 보관용으로 유지'
                    ]
                },
                {
                    subtitle: '문의 상태',
                    description: '문의는 다음 상태로 관리됩니다:\n\n📝 문의: 새로 등록된 상태\n🔄 진행중: 처리 중 (수정/삭제 불가)\n⏸️ 보류: 추가 확인 필요\n✅ 완료: 처리 완료',
                    notes: [
                        '진행중 상태일 때는 수정/삭제가 제한됩니다',
                        '상태 변경이 필요하면 댓글로 요청하세요'
                    ]
                }
            ]
        },
        // ==================== 8. 파일 관리 ====================
        {
            id: 'files',
            title: '파일 관리',
            badge: '설정',
            subsections: [
                {
                    subtitle: '파일 관리 개요',
                    description: 'ChromaDB(벡터 데이터베이스)에 저장된 문서 청크를 확인할 수 있는 페이지입니다.\n\n💡 벡터 데이터베이스는 AI가 답변을 생성할 때 참고하는 데이터가 저장된 곳입니다.',
                    image: '/guide/files.gif',
                    notes: [
                        '벡터화된 문서 청크 조회',
                        '청크 내용 상세 보기',
                        '총 문서 수 확인'
                    ]
                },
                {
                    subtitle: '벡터화된 문서 청크',
                    description: '등록된 문서들을 확인할 수 있습니다:\n\n• ID: 문서 고유 식별자\n• 파일명/제목: 원본 문서 정보\n• 내용 미리보기: 청크 내용의 일부\n• 상세보기: 전체 내용 확인',
                    notes: [
                        '페이지네이션으로 편리하게 조회',
                        '클릭하여 상세 내용 확인 가능'
                    ]
                }
            ]
        }
    ];

    return (
        <Container maxW="container.xl" py={8}>
            {/* 헤더 */}
            <VStack spacing={6} align="stretch" mb={8}>
                <HStack>
                    <InfoIcon boxSize={8} color={accentColor} />
                    <Heading size="xl">BEXCO AI 챗봇 관리자 가이드</Heading>
                </HStack>
                <Text fontSize="lg" color="gray.600">
                    BEXCO 담당자를 위한 관리자 페이지 사용 안내서입니다.
                </Text>
                
                {/* 메뉴 구조 */}
                <Box p={4} bg="blue.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.500">
                    <Text fontWeight="bold" mb={3}>📌 메뉴 구조</Text>
                    <VStack align="stretch" spacing={2} fontSize="sm" pl={2}>
                        <Text>• <strong>대시보드</strong>: AI 챗봇 시스템 현황 및 통계 확인</Text>
                        <Text>• <strong>📁 로그</strong>:</Text>
                        <Text pl={4}>- 쿼리 로그: 사용자 질문/답변 이력 분석 및 검증 관리</Text>
                        <Text pl={4}>- 크롤링 로그: 웹사이트 자동 수집 현황 확인</Text>
                        <Text>• <strong>📁 리포트</strong>:</Text>
                        <Text pl={4}>- 검증 리포트: AI 응답 품질 테스트 실행 및 결과 확인</Text>
                        <Text>• <strong>📁 게시판</strong>:</Text>
                        <Text pl={4}>- 공지사항: 시스템 관련 중요 안내사항 확인</Text>
                        <Text pl={4}>- 자주하는 질문: FAQ 등록/수정/삭제 (카테고리별 5개 제한)</Text>
                        <Text pl={4}>- 문의게시판: 관리자와의 소통 채널</Text>
                        <Text>• <strong>📁 설정</strong>:</Text>
                        <Text pl={4}>- 파일 관리: 벡터 DB에 저장된 문서 청크 조회</Text>
                        <Text>• <strong>사용자가이드</strong>: 이 페이지</Text>
                    </VStack>
                </Box>

                {/* 용어 설명 */}
                <Box p={4} bg="purple.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="purple.500">
                    <Text fontWeight="bold" mb={3}>📖 주요 용어 설명</Text>
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th>용어</Th>
                                <Th>설명</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            <Tr>
                                <Td fontWeight="medium">쿼리 (Query)</Td>
                                <Td>사용자가 챗봇에 입력한 질문</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="medium">신뢰도 (Confidence)</Td>
                                <Td>AI가 답변에 대해 얼마나 확신하는지 (0~100%, 높을수록 좋음)</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="medium">처리시간</Td>
                                <Td>AI가 답변을 생성하는 데 걸린 시간 (밀리초, ms)</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="medium">청크 (Chunk)</Td>
                                <Td>문서를 AI가 처리하기 좋게 나눈 작은 조각</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="medium">벡터 DB</Td>
                                <Td>AI가 답변을 찾을 때 참고하는 데이터 저장소</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="medium">크롤링</Td>
                                <Td>웹사이트에서 자동으로 정보를 수집하는 작업</Td>
                            </Tr>
                        </Tbody>
                    </Table>
                </Box>

                <Divider />
            </VStack>

            {/* 아코디언 메뉴 */}
            <Accordion allowMultiple >
                {guideData.map((section, idx) => (
                    <AccordionItem
                        key={section.id}
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius="lg"
                        mb={4}
                        bg={bgColor}
                    >
                        <h2>
                            <AccordionButton
                                py={4}
                                px={6}
                                _hover={{ bg: hoverBg }}
                            >
                                <HStack flex="1" textAlign="left" spacing={3}>
                                    <Text fontWeight="bold" fontSize="lg">
                                        {idx + 1}. {section.title}
                                    </Text>
                                    {section.badge && (
                                        <Badge colorScheme="purple" fontSize="xs">
                                            {section.badge}
                                        </Badge>
                                    )}
                                </HStack>
                                <AccordionIcon />
                            </AccordionButton>
                        </h2>
                        <AccordionPanel pb={6} px={6}>
                            <VStack align="stretch" spacing={6}>
                                {section.subsections.map((subsection, subIdx) => (
                                    <Box key={subIdx}>
                                        {/* 소제목 */}
                                        {subsection.subtitle && (
                                            <Heading size="md" mb={3} color={accentColor}>
                                                {subsection.subtitle}
                                            </Heading>
                                        )}

                                        {/* 설명 텍스트 */}
                                        <Text
                                            mb={4}
                                            whiteSpace="pre-line"
                                            lineHeight="tall"
                                            color="gray.700"
                                        >
                                            {subsection.description}
                                        </Text>

                                        {/* 스크린샷 이미지 */}
                                        {subsection.image && (
                                            <Box
                                                mb={4}
                                                borderRadius="md"
                                                overflow="hidden"
                                                border="1px solid"
                                                borderColor={borderColor}
                                                boxShadow="sm"
                                            >
                                                <Image
                                                    src={subsection.image}
                                                    alt={subsection.subtitle || '가이드 이미지'}
                                                    w="100%"
                                                    fallback={
                                                        <Box
                                                            bg="gray.100"
                                                            h="300px"
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                        >
                                                            <Text color="gray.500">
                                                                이미지를 준비 중입니다...
                                                            </Text>
                                                        </Box>
                                                    }
                                                />
                                            </Box>
                                        )}

                                        {/* 참고사항 */}
                                        {subsection.notes && subsection.notes.length > 0 && (
                                            <Box
                                                p={4}
                                                bg={noteBoxBg}
                                                borderRadius="md"
                                                borderLeft="4px solid"
                                                borderLeftColor={accentColor}
                                            >
                                                <Text fontWeight="bold" mb={2} color={accentColor}>
                                                    💡 참고사항
                                                </Text>
                                                <VStack align="stretch" spacing={1}>
                                                    {subsection.notes.map((note, noteIdx) => (
                                                        <Text key={noteIdx} fontSize="sm" pl={2}>
                                                            • {note}
                                                        </Text>
                                                    ))}
                                                </VStack>
                                            </Box>
                                        )}

                                        {/* 구분선 (마지막 항목 제외) */}
                                        {subIdx < section.subsections.length - 1 && (
                                            <Divider mt={6} />
                                        )}
                                    </Box>
                                ))}
                            </VStack>
                        </AccordionPanel>
                    </AccordionItem>
                ))}
            </Accordion>

            {/* 푸터 */}
            <Box mt={12} p={6} bg={bgColor} borderRadius="lg" border="1px solid" borderColor={borderColor}>
                <VStack spacing={3}>
                    <Text fontWeight="bold" fontSize="lg">
                        추가 문의사항이나 궁금하신점이 있으시면 문의게시판을 이용해주세요.
                    </Text>
                    <Text color="gray.600" fontSize="sm">
                        BEXCO AI 챗봇 관리 시스템 v1.0
                    </Text>
                </VStack>
            </Box>
        </Container>
    );
};

export default UserGuidePage2;

